import React, { memo, useEffect, useMemo, useState } from "react";
import styles from "./UserInfoDropdown.module.scss";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { logOut } from "../../../stores/slices/current-user-slice";
import { ApplicationRoutes } from "../../../routes";
import { RootState } from "../../../stores/store";
import { UserType } from "../../../model/userType";
import { useLogoutMutation } from "../../../service/authentication-service";

type Props = {};

const UserInfoDropdown: React.FC<Props> = memo((props: Props) => {
	const [showDropdown, setShowDropdown] = useState(false);

	const userInfo = useSelector((state: RootState) => state.user);

	const dispatch = useDispatch();

	const navigator = useNavigate();

	const [logoutApi] = useLogoutMutation();

	const userDisplay = useMemo(() => {
		if (userInfo?.userType === UserType.Customer || userInfo?.userType === UserType.Provider) {
			if (userInfo.userName) {
				return `Welcome, ${userInfo?.userName}`;
			}
		}
		return userInfo?.userType;
	}, [userInfo]);

	function toggleDropdown() {
		setShowDropdown(!showDropdown);
	}

	async function logout() {
		await logoutApi();
		dispatch(logOut());
		navigator(ApplicationRoutes.LANDING_PAGE);
	}

	function closeDropdown(event: React.FocusEvent<HTMLDivElement>) {
		if (event.relatedTarget) return;

		setShowDropdown(false);
	}

	return (
		<div className={styles.container} onBlur={closeDropdown}>
			<button className={`standardButton ${styles.button}`} onClick={toggleDropdown}>
				<div className={styles.icon}>
					<svg width="18" height="19" viewBox="0 0 18 19" fill="none" xmlns="http://www.w3.org/2000/svg">
						<path
							d="M8.99997 9.14021C7.87707 9.14021 6.9158 8.7404 6.11615 7.94076C5.31649 7.14111 4.91667 6.17983 4.91667 5.05694C4.91667 3.93402 5.31649 2.97275 6.11615 2.17311C6.9158 1.37346 7.87707 0.973633 8.99997 0.973633C10.1229 0.973633 11.0841 1.37346 11.8838 2.17311C12.6834 2.97275 13.0833 3.93402 13.0833 5.05694C13.0833 6.17983 12.6834 7.14111 11.8838 7.94076C11.0841 8.7404 10.1229 9.14021 8.99997 9.14021ZM0.25 18.0248V15.4313C0.25 14.8599 0.405186 14.3308 0.715558 13.844C1.02591 13.3571 1.44059 12.9828 1.9596 12.721C3.11281 12.1556 4.27612 11.7316 5.44951 11.4489C6.62291 11.1662 7.80639 11.0249 8.99997 11.0249C10.1935 11.0249 11.377 11.1662 12.5504 11.4489C13.7238 11.7316 14.8871 12.1556 16.0403 12.721C16.5593 12.9828 16.974 13.3571 17.2844 13.844C17.5948 14.3308 17.7499 14.8599 17.7499 15.4313V18.0248H0.25ZM1.99997 16.2748H16V15.4313C16 15.1949 15.9315 14.9762 15.7947 14.775C15.6578 14.5738 15.472 14.4097 15.2371 14.2825C14.232 13.7875 13.2071 13.4124 12.1625 13.1574C11.1178 12.9024 10.0636 12.7748 8.99997 12.7748C7.93632 12.7748 6.88216 12.9024 5.83749 13.1574C4.79281 13.4124 3.76792 13.7875 2.7628 14.2825C2.52796 14.4097 2.34212 14.5738 2.20527 14.775C2.06841 14.9762 1.99997 15.1949 1.99997 15.4313V16.2748ZM8.99997 7.39027C9.64164 7.39027 10.1909 7.1618 10.6479 6.70485C11.1048 6.24791 11.3333 5.6986 11.3333 5.05694C11.3333 4.41527 11.1048 3.86597 10.6479 3.40902C10.1909 2.95208 9.64164 2.7236 8.99997 2.7236C8.3583 2.7236 7.809 2.95208 7.35205 3.40902C6.89511 3.86597 6.66664 4.41527 6.66664 5.05694C6.66664 5.6986 6.89511 6.24791 7.35205 6.70485C7.809 7.1618 8.3583 7.39027 8.99997 7.39027Z"
							fill="#1C1B1F"
						/>
					</svg>
				</div>
				<div className={styles.user}>{userDisplay}</div>
				<svg width="12" height="7" viewBox="0 0 12 7" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path
						d="M1.00005 0.669699C1.18741 0.483448 1.44087 0.378906 1.70505 0.378906C1.96924 0.378906 2.22269 0.483448 2.41005 0.669699L6.00005 4.2097L9.54005 0.669699C9.72741 0.483448 9.98087 0.378906 10.2451 0.378906C10.5092 0.378906 10.7627 0.483448 10.9501 0.669699C11.0438 0.762662 11.1182 0.873263 11.1689 0.995122C11.2197 1.11698 11.2458 1.24769 11.2458 1.3797C11.2458 1.51171 11.2197 1.64242 11.1689 1.76428C11.1182 1.88613 11.0438 1.99674 10.9501 2.0897L6.71005 6.3297C6.61709 6.42343 6.50649 6.49782 6.38463 6.54859C6.26277 6.59936 6.13206 6.6255 6.00005 6.6255C5.86804 6.6255 5.73733 6.59936 5.61547 6.54859C5.49362 6.49782 5.38301 6.42343 5.29005 6.3297L1.00005 2.0897C0.906323 1.99674 0.831929 1.88613 0.78116 1.76428C0.730392 1.64242 0.704252 1.51171 0.704252 1.3797C0.704252 1.24769 0.730392 1.11698 0.78116 0.995122C0.831929 0.873263 0.906323 0.762662 1.00005 0.669699Z"
						fill="#525252"
					/>
				</svg>
			</button>
			<div className={`${styles.dropdown} ${showDropdown ? styles.show : ""}`}>
				<button className={`standardButton ${styles.item}`} onClick={logout}>
					Logout
					<svg width="20" height="16" viewBox="0 0 20 16" fill="none" xmlns="http://www.w3.org/2000/svg">
						<path
							d="M16.7815 8.70586H5.68103V7.29414H16.7815L15.0692 5.5819L16.0611 4.5611L19.5 8L16.0611 11.4389L15.0692 10.4181L16.7815 8.70586ZM11.9163 5.04976V1.41175H2.31445V14.5883H11.9163V10.9502H13.328V16H0.90271V0H13.328V5.04976H11.9163Z"
							fill="#2E3336"
						/>
					</svg>
				</button>
			</div>
		</div>
	);
});

export default UserInfoDropdown;
